// Offerwall and survey networks: how each one reaches us (postback), how it looks on the Earn
// page (wall), and how long its rewards stay pending (hold).
import { useId, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router';
import {
  ArrowRight,
  Cable,
  Check,
  CircleAlert,
  FlaskConical,
  Minus,
  Pencil,
  Plus,
  ShieldAlert,
  X,
} from 'lucide-react';
import { api } from '../lib/api';
import { useCan } from '../lib/staff';
import { useAssistantContext } from '../lib/assistant';
import {
  confirmAction,
  CopyButton,
  Drawer,
  EmptyState,
  ErrorNote,
  fmtNum,
  fmtUsd,
  Initial,
  KV,
  PageHeader,
  Pill,
  toast,
} from '../components/ui';
import { PostbackForm } from '../components/PostbackForm';
import { Advanced, Disclosure, Field, HoldForm, Segmented, WallForm } from '../components/WallForm';

interface Provider {
  id: string;
  name: string;
  kind: string;
  status: 'active' | 'paused' | 'disabled';
  postbackPath: string;
  postback: Record<string, unknown> & { path: string };
  wall: Record<string, unknown> | null;
  coinsPerUsd: number;
  /** Counts as premium for the Android app's AppsFlyer premium_earning event. */
  premium?: boolean;
  holdPolicy: { tiers: { minCoins: number; days: number }[] } | null;
  /** Offers straight on Earn (Admin → Offers on Earn); the key is never sent here. */
  offersApi?: { enabled: boolean; hasKey: boolean } | null;
  stats24h: { verified: number; rejected: number };
}

const KINDS: [string, string][] = [
  ['offerwall', 'Offerwall'],
  ['survey', 'Surveys'],
  ['video', 'Videos'],
  ['game', 'Games'],
  ['cpa', 'Single offers (CPA)'],
];
const kindLabel = (k: string) => KINDS.find(([x]) => x === k)?.[1] ?? k;

const STATUSES = [
  {
    key: 'paused',
    label: 'Paused',
    help: 'Postbacks are logged and refused. Nothing is credited.',
  },
  {
    key: 'active',
    label: 'Live',
    help: 'Postbacks credit coins to users straight away.',
    activeClass: 'bg-mint text-ink shadow-[0_1px_3px_rgba(17,17,16,0.12)]',
  },
  { key: 'disabled', label: 'Disabled', help: 'Hidden everywhere, and postbacks are refused.' },
] as const;

const EMPTY: Omit<Provider, 'stats24h' | 'postbackPath'> = {
  id: '',
  name: '',
  kind: 'offerwall',
  status: 'paused',
  postback: {
    path: '',
    methods: ['GET'],
    source: 'query',
    auth: { key: { location: 'query', name: 'key', value: '' } },
    fields: {
      userId: 'user_id',
      txid: 'transaction_id',
      amount: 'amount',
      revenueUsd: 'payout',
      offerId: 'offer_id',
      offerName: 'offer_name',
      ip: 'ip',
      country: 'country',
    },
    reversal: { statusField: 'status', reversalValues: ['rejected', 'reversed', 'chargeback'] },
    response: { body: 'ok' },
  },
  wall: {
    urlTemplate: 'https://wall.example.com/{placement_id}/{user_id}',
    params: { placement_id: '' },
    display: { title: '' },
  },
  coinsPerUsd: 1000,
  premium: false,
  holdPolicy: null,
};

export function Providers() {
  const { data, error, isLoading, refetch } = useQuery({
    queryKey: ['providers'],
    queryFn: () => api<{ postbackBase: string; providers: Provider[] }>('/providers'),
  });
  const [editing, setEditing] = useState<Omit<Provider, 'stats24h' | 'postbackPath'> | null>(null);
  // Editing networks (and test postbacks): providers.manage. Adding one sets its key, so it needs
  // providers.secrets too. Everyone else reads.
  const can = useCan();
  const manage = can('providers.manage');
  const add = manage && can('providers.secrets');
  useAssistantContext({ providerId: editing?.id });
  const list = data?.providers ?? [];
  const order = { active: 0, paused: 1, disabled: 2 } as const;
  const sorted = [...list].sort((a, b) => order[a.status] - order[b.status]);
  return (
    <div className="space-y-5">
      <PageHeader
        title="Networks"
        description={
          <>
            Offerwalls and survey partners. Each one tells us a user earned by calling a postback
            URL under{' '}
            <code className="whitespace-nowrap rounded-[6px] bg-paper px-1.5 py-0.5 text-[13px] text-ink">
              {data?.postbackBase ?? '…/postback/'}
            </code>{' '}
            and must prove who it is with a shared key, a list of allowed IPs, or both.
          </>
        }
        actions={
          add && (
            <button className="btn-primary" onClick={() => setEditing(structuredClone(EMPTY))}>
              <Plus size={16} aria-hidden="true" /> Add network
            </button>
          )
        }
      />
      <ErrorNote error={error} onRetry={() => void refetch()} />
      {error && !data ? null : isLoading ? (
        <div className="grid gap-4 xl:grid-cols-2" aria-busy="true">
          {[0, 1, 2, 3].map((i) => (
            <div key={i} className="card space-y-4 p-5">
              <div className="flex items-center gap-3">
                <div className="h-11 w-11 animate-pulse rounded-full bg-ink/[0.07]" />
                <div className="flex-1 space-y-2">
                  <div className="h-4 w-1/3 animate-pulse rounded bg-ink/[0.08]" />
                  <div className="h-3 w-1/2 animate-pulse rounded bg-ink/[0.05]" />
                </div>
              </div>
              <div className="h-9 animate-pulse rounded-[10px] bg-ink/[0.05]" />
              <div className="h-4 w-2/3 animate-pulse rounded bg-ink/[0.05]" />
            </div>
          ))}
        </div>
      ) : list.length === 0 ? (
        <div className="card">
          <EmptyState
            icon={Cable}
            title="No networks yet"
            action={
              add && (
                <button
                  className="btn-primary h-10 py-0 text-[14px]"
                  onClick={() => setEditing(structuredClone(EMPTY))}
                >
                  <Plus size={15} aria-hidden="true" /> Add network
                </button>
              )
            }
          >
            Add your first offerwall, then dry-run a postback before you put it live.
          </EmptyState>
        </div>
      ) : (
        <div className="grid gap-4 xl:grid-cols-2">
          {sorted.map((p) => (
            <ProviderCard
              key={p.id}
              p={p}
              base={data!.postbackBase}
              {...(manage ? { onEdit: () => setEditing(structuredClone(p)) } : {})}
            />
          ))}
        </div>
      )}
      {editing && (
        <ProviderEditor
          initial={editing}
          base={data?.postbackBase ?? ''}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}

function authState(pb: Provider['postback']) {
  const a = (pb.auth ?? {}) as {
    key?: { value?: string; name?: string };
    ipAllowlist?: string[];
    signature?: unknown;
  };
  const key = !!a.key?.value;
  const ips = a.ipAllowlist?.length ?? 0;
  const sig = !!a.signature;
  return { key, keyName: a.key?.name, ips, sig, ok: key || ips > 0 };
}

function ProviderCard({ p, base, onEdit }: { p: Provider; base: string; onEdit?: () => void }) {
  const auth = authState(p.postback);
  const display = (p.wall as { display?: { title?: string; logoUrl?: string } } | null)?.display;
  const total = p.stats24h.verified + p.stats24h.rejected;
  const refusedShare = total ? p.stats24h.rejected / total : 0;
  return (
    <article className="card flex min-w-0 flex-col" aria-label={p.name}>
      <div className="flex flex-1 flex-col p-4 sm:p-5">
        <div className="flex items-start gap-3">
          {display?.logoUrl ? (
            <img
              src={display.logoUrl}
              alt=""
              className="h-11 w-11 shrink-0 rounded-[12px] border border-hairline bg-paper object-contain p-1"
            />
          ) : (
            <Initial name={p.name} size={44} />
          )}
          <div className="min-w-0 flex-1">
            <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
              <h2 className="min-w-0 truncate text-[17px] font-extrabold leading-snug tracking-[-0.01em]">
                {p.name}
              </h2>
              <Pill
                tone={p.status === 'active' ? 'mint' : p.status === 'paused' ? 'sun' : 'neutral'}
              >
                {p.status === 'active' ? 'Live' : p.status === 'paused' ? 'Paused' : 'Disabled'}
              </Pill>
            </div>
            <div className="mt-0.5 text-[13px] text-stone">
              {kindLabel(p.kind)} · {fmtNum(p.coinsPerUsd)} coins per $1
              {p.wall ? '' : ' · not on the Earn page'}
              {p.offersApi?.enabled && (
                <>
                  {' · '}
                  <Link
                    to="/offers?tab=setup"
                    className="font-semibold text-graphite underline-offset-2 hover:text-ink hover:underline"
                  >
                    offers on Earn
                  </Link>
                </>
              )}
            </div>
          </div>
          {onEdit && (
            <button
              className="btn-ghost h-10 shrink-0 px-3.5 py-0 text-[13px] sm:h-9"
              onClick={onEdit}
              aria-label={`Edit ${p.name}`}
            >
              <Pencil size={13} aria-hidden="true" /> Edit
            </button>
          )}
        </div>

        {!auth.ok && (
          <div
            role="alert"
            className="mt-4 flex items-start gap-2.5 rounded-[12px] border border-berry/30 bg-berry/[0.06] px-3.5 py-2.5 text-[13px] leading-snug"
          >
            <ShieldAlert size={16} className="mt-px shrink-0 text-berry-deep" aria-hidden="true" />
            <span>
              <b className="text-berry-deep">Every postback is refused.</b>{' '}
              {auth.sig
                ? 'A signature alone isn’t enough. Add a shared key, or the network’s server IPs, under Edit → Postback.'
                : 'Add a shared key, or the network’s server IPs, under Edit → Postback.'}
            </span>
          </div>
        )}

        <ul className="mt-4 flex flex-wrap gap-1.5" aria-label="How postbacks are checked">
          <AuthChip
            on={auth.key}
            label={auth.key ? 'Shared key' : 'No key'}
            title={auth.key ? `Sent as “${auth.keyName ?? 'key'}”` : undefined}
          />
          <AuthChip
            on={auth.ips > 0}
            label={auth.ips ? `${auth.ips} allowed IP${auth.ips === 1 ? '' : 's'}` : 'Any IP'}
          />
          {auth.sig && <AuthChip on label="Signature checked" />}
        </ul>

        <div className="mt-4 flex items-center gap-1 rounded-[10px] bg-canvas py-1 pl-3 pr-1">
          <code
            className="min-w-0 flex-1 truncate text-[12px] text-graphite"
            title={`${base}${p.postbackPath}`}
          >
            {base}
            {p.postbackPath}
          </code>
          <CopyButton text={`${base}${p.postbackPath}`} label="Copy" />
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-hairline px-4 py-3 text-[13px] sm:px-5">
        <span className="text-stone">Last 24h</span>
        <span className="whitespace-nowrap">
          <b className="tabular-nums">{fmtNum(p.stats24h.verified)}</b>{' '}
          <span className="text-stone">accepted</span>
        </span>
        <Link
          to={`/postbacks?provider=${p.id}&verified=false`}
          className={`-my-1 whitespace-nowrap rounded-[6px] py-1 underline-offset-2 hover:underline ${p.stats24h.rejected ? 'font-semibold text-berry-deep' : 'text-stone'}`}
        >
          <b className="tabular-nums">{fmtNum(p.stats24h.rejected)}</b> refused
          {refusedShare > 0.2 && total >= 5 && (
            <span className="ml-1 rounded-full bg-berry/10 px-1.5 py-px text-[12px] font-bold">
              {Math.round(refusedShare * 100)}%
            </span>
          )}
        </Link>
        <Link
          to={`/postbacks?provider=${p.id}`}
          className="-my-1 ml-auto inline-flex items-center gap-1 whitespace-nowrap rounded-[6px] py-1 font-semibold text-graphite transition hover:text-ink"
        >
          Postback log <ArrowRight size={13} aria-hidden="true" />
        </Link>
      </div>
    </article>
  );
}

function AuthChip({
  on,
  label,
  title,
}: {
  on: boolean;
  label: string;
  title?: string | undefined;
}) {
  return (
    <li
      title={title}
      className={`inline-flex h-6 items-center gap-1 rounded-full px-2.5 text-[12px] font-bold ${on ? 'bg-mint/15 text-mint-deep' : 'bg-ink/[0.06] text-graphite'}`}
    >
      {on ? <Check size={12} aria-hidden="true" /> : <Minus size={12} aria-hidden="true" />}
      {label}
    </li>
  );
}

function ProviderEditor({
  initial,
  base,
  onClose,
}: {
  initial: Omit<Provider, 'stats24h' | 'postbackPath'>;
  base: string;
  onClose: () => void;
}) {
  const [advanced, setAdvanced] = useState(false);
  const uid = useId();
  const qc = useQueryClient();
  const [p, setP] = useState(initial);
  const [postbackText, setPostbackText] = useState(JSON.stringify(initial.postback, null, 2));
  const [wallText, setWallText] = useState(
    initial.wall ? JSON.stringify(initial.wall, null, 2) : '',
  );
  const [holdText, setHoldText] = useState(
    initial.holdPolicy ? JSON.stringify(initial.holdPolicy, null, 2) : '',
  );
  const [err, setErr] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const dirty =
    JSON.stringify(p) !== JSON.stringify(initial) ||
    postbackText !== JSON.stringify(initial.postback, null, 2) ||
    wallText !== (initial.wall ? JSON.stringify(initial.wall, null, 2) : '') ||
    holdText !== (initial.holdPolicy ? JSON.stringify(initial.holdPolicy, null, 2) : '');

  const close = async () => {
    if (
      dirty &&
      (await confirmAction({
        title: 'Discard your changes?',
        body: 'Nothing you changed here has been saved.',
        confirmLabel: 'Discard',
        tone: 'danger',
      })) === null
    )
      return;
    onClose();
  };

  const save = async () => {
    setErr(null);
    let postback: Provider['postback'];
    let wall: Provider['wall'] = null;
    let holdPolicy: Provider['holdPolicy'] = null;
    try {
      postback = JSON.parse(postbackText);
      if (wallText.trim()) wall = JSON.parse(wallText);
      if (holdText.trim()) holdPolicy = JSON.parse(holdText);
    } catch (e) {
      return setErr(`The advanced JSON has a mistake: ${(e as Error).message}`);
    }
    if (!initial.id && !/^[a-z0-9_-]{2,32}$/.test(p.id))
      return setErr('Id: 2–32 lowercase letters, numbers, _ or -.');
    if (!Number.isInteger(p.coinsPerUsd) || p.coinsPerUsd < 1)
      return setErr('Coins per $1 must be a whole number of at least 1.');
    if (!p.name.trim()) return setErr('Give the network a name.');
    if (p.status === 'active' && initial.status !== 'active') {
      const ok = await confirmAction({
        title: `Put ${p.name} live?`,
        body: 'Its postbacks will credit coins to users straight away. Make sure a dry run passes first.',
        confirmLabel: 'Go live',
        tone: 'coin',
      });
      if (ok === null) return;
    }
    setSaving(true);
    try {
      await api(`/providers/${p.id}`, {
        method: 'PUT',
        body: {
          id: p.id,
          name: p.name,
          kind: p.kind,
          status: p.status,
          postback,
          wall,
          coinsPerUsd: p.coinsPerUsd,
          premium: !!p.premium,
          holdPolicy,
        },
      });
      await qc.invalidateQueries({ queryKey: ['providers'] });
      toast(initial.id ? 'Network saved.' : 'Network added. Run a dry test before going live.');
      onClose();
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setSaving(false);
    }
  };

  const status = STATUSES.find((x) => x.key === p.status) ?? STATUSES[0];
  const goingLive = p.status === 'active' && initial.status !== 'active';

  return (
    <Drawer
      onClose={close}
      title={initial.id ? initial.name : 'New network'}
      subtitle={
        initial.id ? (
          <span className="break-all font-mono text-[12px]">
            {base}
            {initial.postback.path || initial.id}
          </span>
        ) : (
          'Fill in the basics and save, then dry-run a postback before going live.'
        )
      }
      footer={
        <div className="space-y-2.5">
          {err && (
            <p
              role="alert"
              className="flex items-start gap-2 rounded-[10px] bg-berry/[0.06] px-3 py-2 text-[13px] font-semibold text-berry-deep"
            >
              <CircleAlert size={15} className="mt-px shrink-0" aria-hidden="true" />
              {err}
            </p>
          )}
          <div className="flex flex-wrap items-center gap-2">
            <button
              className="btn-primary h-11 py-0"
              onClick={save}
              disabled={saving || (!dirty && !!initial.id)}
            >
              {saving ? 'Saving…' : initial.id ? 'Save changes' : 'Add network'}
            </button>
            <button className="btn-ghost h-11 py-0" onClick={close}>
              Cancel
            </button>
            {dirty && (
              <span className="ml-auto inline-flex items-center gap-1.5 text-[12px] font-semibold text-graphite">
                <span className="h-2 w-2 rounded-full bg-coin" aria-hidden="true" />
                Unsaved changes
              </span>
            )}
          </div>
        </div>
      }
    >
      <div className="space-y-10">
        <section aria-labelledby={`${uid}-basics`}>
          <SectionTitle id={`${uid}-basics`} title="Basics" />
          <div className="grid gap-x-4 gap-y-5 sm:grid-cols-2">
            <Field
              id={`${uid}-id`}
              label="ID"
              hint={
                initial.id
                  ? 'Fixed once created.'
                  : 'Lowercase letters, numbers, _ or -. Used in the postback URL.'
              }
            >
              <input
                id={`${uid}-id`}
                className="input h-11 py-0 font-mono text-[14px] disabled:cursor-not-allowed disabled:bg-canvas disabled:text-graphite"
                value={p.id}
                disabled={!!initial.id}
                placeholder="e.g. adgem"
                spellCheck={false}
                autoComplete="off"
                onChange={(e) => setP({ ...p, id: e.target.value.toLowerCase().trim() })}
              />
            </Field>
            <Field id={`${uid}-name`} label="Name" hint="Shown to staff and in the postback log.">
              <input
                id={`${uid}-name`}
                className="input h-11 py-0"
                value={p.name}
                onChange={(e) => setP({ ...p, name: e.target.value })}
              />
            </Field>
            <Field id={`${uid}-kind`} label="Type">
              <select
                id={`${uid}-kind`}
                className="input h-11 py-0"
                value={p.kind}
                onChange={(e) => setP({ ...p, kind: e.target.value })}
              >
                {KINDS.map(([k, label]) => (
                  <option key={k} value={k}>
                    {label}
                  </option>
                ))}
              </select>
            </Field>
            <Field
              id={`${uid}-cpu`}
              label="Coins per $1"
              hint="For rewards the network reports in US dollars, and its offers on Earn."
            >
              <div className="flex h-11 items-center rounded-[10px] border border-hairline bg-paper transition focus-within:border-ink focus-within:shadow-[0_0_0_3px_var(--color-coin-tint)]">
                <input
                  id={`${uid}-cpu`}
                  className="h-full w-full min-w-0 rounded-[10px] bg-transparent pl-3.5 pr-2 text-[15px] font-semibold tabular-nums outline-none focus-visible:outline-none"
                  inputMode="numeric"
                  value={Number.isFinite(p.coinsPerUsd) ? p.coinsPerUsd : ''}
                  onChange={(e) =>
                    setP({ ...p, coinsPerUsd: Number(e.target.value.replace(/\D/g, '')) })
                  }
                />
                <span className="pr-3.5 text-[14px] font-semibold text-stone" aria-hidden="true">
                  coins
                </span>
              </div>
            </Field>
          </div>
          <div className="mt-5">
            <Field label="Status" hint={status.help}>
              <Segmented
                label="Status"
                value={p.status}
                onChange={(k) => setP({ ...p, status: k })}
                options={STATUSES.map((x) => ({
                  key: x.key,
                  label: x.label,
                  ...('activeClass' in x ? { activeClass: x.activeClass } : {}),
                }))}
              />
            </Field>
            {goingLive && (
              <p className="mt-2 flex items-start gap-2 rounded-[10px] bg-sun/25 px-3 py-2 text-[13px] font-semibold text-ink">
                <ShieldAlert size={15} className="mt-px shrink-0" aria-hidden="true" />
                Going live means real coins. You’ll be asked to confirm when you save.
              </p>
            )}
          </div>
          <div className="mt-5">
            <Field
              label="App ad attribution"
              hint={
                <>
                  Premium: a user’s first credit from this network sends AppsFlyer’s premium_earning
                  event (Android app users only). To mark single postbacks instead, map an extra
                  field named <code className="font-mono">premium</code> (true or 1), like AdBreak
                  Media’s isPremium.
                </>
              }
            >
              <Segmented
                label="App ad attribution"
                value={p.premium ? 'premium' : 'standard'}
                onChange={(k) => setP({ ...p, premium: k === 'premium' })}
                options={[
                  { key: 'standard', label: 'Standard' },
                  { key: 'premium', label: 'Premium' },
                ]}
              />
            </Field>
          </div>
        </section>

        <section aria-labelledby={`${uid}-pb`}>
          <SectionTitle
            id={`${uid}-pb`}
            title="Postback"
            sub="The call a network makes to tell us a user earned, and how we check it’s really them."
          />
          <PostbackForm
            text={postbackText}
            onText={setPostbackText}
            base={base}
            providerId={p.id}
            saved={!!initial.id}
          />
          <Disclosure
            label="Edit as JSON"
            hint="methods, source, IP allowlist, signature, response"
            open={advanced}
            onToggle={() => setAdvanced((a) => !a)}
          >
            <textarea
              className="input h-80 font-mono text-xs leading-relaxed"
              spellCheck={false}
              aria-label="Postback settings as JSON"
              value={postbackText}
              onChange={(e) => setPostbackText(e.target.value)}
            />
            <p className="mt-1.5 text-[12px] leading-snug text-stone">
              Secrets show as ••••••••; leave them as they are to keep the stored value. Postbacks
              are refused unless the network sends the key or calls from an allowed IP
              (auth.ipAllowlist); a signature alone is not enough.
            </p>
          </Disclosure>
        </section>

        <section aria-labelledby={`${uid}-wall`}>
          <SectionTitle
            id={`${uid}-wall`}
            title="Wall"
            sub="How this network looks and opens on the Earn page."
          />
          <WallForm text={wallText} onText={setWallText} kind={p.kind} />
          <Advanced label="Edit as JSON" hint="empty means no wall">
            <textarea
              className="input h-56 font-mono text-xs leading-relaxed"
              spellCheck={false}
              aria-label="Wall settings as JSON"
              value={wallText}
              placeholder="Empty means no wall"
              onChange={(e) => setWallText(e.target.value)}
            />
          </Advanced>
        </section>

        <section aria-labelledby={`${uid}-hold`}>
          <SectionTitle
            id={`${uid}-hold`}
            title="Hold"
            sub="How long new rewards stay pending before they can be cashed out."
          />
          <HoldForm text={holdText} onText={setHoldText} />
          <Advanced label="Edit as JSON" hint="empty means the standard policy">
            <textarea
              className="input h-28 font-mono text-xs leading-relaxed"
              spellCheck={false}
              aria-label="Hold policy as JSON"
              placeholder="Empty means the standard policy"
              value={holdText}
              onChange={(e) => setHoldText(e.target.value)}
            />
          </Advanced>
        </section>

        {initial.id && <PostbackTester id={initial.id} dirty={dirty} />}
      </div>
    </Drawer>
  );
}

function SectionTitle({ id, title, sub }: { id: string; title: string; sub?: string }) {
  return (
    <div className="mb-4 border-b border-hairline pb-2.5">
      <h3 id={id} className="text-[18px] font-extrabold tracking-[-0.015em]">
        {title}
      </h3>
      {sub && <p className="mt-0.5 text-[13px] leading-snug text-stone">{sub}</p>}
    </div>
  );
}

interface TestResult {
  fields: Record<string, string>;
  parsed: {
    kind: string;
    userId: string | null;
    clickId: string | null;
    externalTxid: string | null;
    coins: number | null;
    revenueMinor: number | null;
    offerName: string | null;
    ip: string | null;
  };
  auth: { ok: boolean; reason: string | null; checks: Record<string, boolean> };
  methodOk: boolean;
  userExists: boolean;
  providerStatus: string;
}

/** Paste a sample postback (full URL or just the query string) to see how the SAVED config reads it. */
function PostbackTester({ id, dirty }: { id: string; dirty: boolean }) {
  const uid = useId();
  const [sample, setSample] = useState('');
  const [method, setMethod] = useState<'GET' | 'POST'>('GET');
  const [body, setBody] = useState('');
  const [ip, setIp] = useState('');
  const [res, setRes] = useState<TestResult | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const test = async () => {
    setErr(null);
    setRes(null);
    try {
      setRes(
        await api<TestResult>(`/providers/${id}/test`, {
          body: { url: sample, method, ...(body ? { body } : {}), ...(ip ? { clientIp: ip } : {}) },
        }),
      );
    } catch (e) {
      setErr((e as Error).message);
    }
  };
  const credited =
    res &&
    res.methodOk &&
    res.auth.ok &&
    res.providerStatus === 'active' &&
    !!res.parsed.externalTxid &&
    (res.parsed.kind === 'reversal' || res.parsed.coins !== null) &&
    res.userExists;
  const failedChecks = res
    ? [
        !res.methodOk && 'method',
        !res.auth.ok && 'authentication',
        res.providerStatus !== 'active' && 'network not live',
        !res.parsed.externalTxid && 'no transaction id',
        !(res.parsed.kind === 'reversal' || res.parsed.coins !== null) && 'no usable amount',
        !(res.parsed.userId && res.userExists) && 'user',
      ].filter((x): x is string => !!x)
    : [];
  const sentence = (t: string) => t.charAt(0).toUpperCase() + t.slice(1);
  return (
    <section
      aria-labelledby={`${uid}-dry`}
      className="rounded-[16px] border border-hairline bg-paper p-4 sm:p-5"
    >
      <div className="flex items-start gap-3">
        <span className="grid h-9 w-9 shrink-0 place-items-center rounded-full bg-sky-500/12 text-sky-800">
          <FlaskConical size={17} aria-hidden="true" />
        </span>
        <div className="min-w-0">
          <h3 id={`${uid}-dry`} className="text-[16px] font-extrabold">
            Dry-run a postback
          </h3>
          <p className="mt-0.5 text-[13px] leading-snug text-stone">
            Paste a URL the network would call. We read it with the <b>saved</b> settings; nothing
            is written or credited.
          </p>
        </div>
      </div>
      {dirty && (
        <p className="mt-3 flex items-start gap-2 rounded-[10px] bg-sun/25 px-3 py-2 text-[13px] font-semibold">
          <CircleAlert size={15} className="mt-px shrink-0" aria-hidden="true" />
          You have unsaved changes. The dry run still uses the last saved version.
        </p>
      )}
      <div className="mt-4">
        <label
          htmlFor={`${uid}-sample`}
          className="mb-1.5 block text-[13px] font-semibold text-graphite"
        >
          Sample postback
        </label>
        <div className="grid gap-2 sm:grid-cols-[96px_minmax(0,1fr)]">
          <select
            className="input h-11 py-0"
            value={method}
            onChange={(e) => setMethod(e.target.value as 'GET' | 'POST')}
            aria-label="Method"
          >
            <option>GET</option>
            <option>POST</option>
          </select>
          <input
            id={`${uid}-sample`}
            className="input h-11 py-0 font-mono text-[13px]"
            placeholder="?key=…&user_id=usr_…&amount=100&transaction_id=T1"
            spellCheck={false}
            value={sample}
            onChange={(e) => setSample(e.target.value)}
          />
        </div>
        <p className="mt-1.5 text-[12px] text-stone">A full URL or just the query string.</p>
      </div>
      {method === 'POST' && (
        <textarea
          className="input mt-2 h-20 font-mono text-xs"
          placeholder="Body (form or JSON)"
          aria-label="Request body"
          value={body}
          onChange={(e) => setBody(e.target.value)}
        />
      )}
      <div className="mt-4 flex flex-wrap items-end gap-3">
        <div className="w-full sm:w-64">
          <label
            htmlFor={`${uid}-ip`}
            className="mb-1.5 block text-[13px] font-semibold text-graphite"
          >
            Pretend it came from IP <span className="font-normal text-stone">(optional)</span>
          </label>
          <input
            id={`${uid}-ip`}
            className="input h-11 py-0 font-mono text-[13px]"
            placeholder="e.g. 18.156.132.55"
            spellCheck={false}
            value={ip}
            onChange={(e) => setIp(e.target.value.trim())}
          />
        </div>
        <button className="btn-primary h-11 py-0" onClick={test} disabled={!sample.trim()}>
          Run dry test
        </button>
      </div>
      <p className="mt-2 text-[12px] leading-snug text-stone">
        This only tests one address. To accept a network’s servers for real, add their IPs to
        auth.ipAllowlist under the postback’s “Edit as JSON”.
      </p>
      {err && (
        <p role="alert" className="mt-3 text-[13px] font-semibold text-berry-deep">
          {err}
        </p>
      )}
      {res && (
        <div className="mt-5 space-y-3" aria-live="polite">
          <div
            className={`flex items-start gap-2.5 rounded-[12px] px-4 py-3 text-[14px] font-bold ${credited ? 'bg-mint/15 text-mint-deep' : 'bg-berry/[0.08] text-berry-deep'}`}
          >
            {credited ? (
              <Check size={17} className="mt-px shrink-0" aria-hidden="true" />
            ) : (
              <X size={17} className="mt-px shrink-0" aria-hidden="true" />
            )}
            <span>
              {credited
                ? res.parsed.kind === 'reversal'
                  ? 'This would reverse an earlier credit.'
                  : `This would credit ${fmtNum(res.parsed.coins ?? 0)} coins.`
                : `This postback would be refused. Failed: ${failedChecks.join(', ')}.`}
            </span>
          </div>
          <ul className="divide-y divide-hairline overflow-hidden rounded-[12px] border border-hairline text-[13px]">
            <Check2 ok={res.methodOk} label="Method allowed" detail={method} />
            <Check2
              ok={res.auth.ok}
              label="Authentication"
              detail={res.auth.ok ? 'Passed' : sentence(res.auth.reason ?? 'Failed')}
            />
            <Check2
              ok={res.providerStatus === 'active'}
              label="Network is live"
              detail={res.providerStatus === 'active' ? 'Live' : `Currently ${res.providerStatus}`}
            />
            <Check2
              ok={!!res.parsed.externalTxid}
              label="Transaction ID"
              detail={res.parsed.externalTxid ?? 'Not found'}
            />
            <Check2
              ok={res.parsed.kind === 'reversal' || res.parsed.coins !== null}
              label={res.parsed.kind === 'reversal' ? 'Reversal' : 'Reward'}
              detail={
                res.parsed.kind === 'reversal'
                  ? 'Takes back the original credit'
                  : res.parsed.coins !== null
                    ? `${fmtNum(res.parsed.coins)} coins`
                    : 'No usable amount'
              }
            />
            <Check2
              ok={!!res.parsed.userId && res.userExists}
              label="User"
              detail={
                res.parsed.userId
                  ? res.userExists
                    ? res.parsed.userId
                    : `${res.parsed.userId}: no such user`
                  : (res.parsed.clickId ?? 'Not found')
              }
            />
          </ul>
          <div className="rounded-[12px] bg-canvas px-4 py-3">
            <KV
              items={[
                ['Offer', res.parsed.offerName ?? '—'],
                [
                  'Payout to us',
                  res.parsed.revenueMinor !== null ? fmtUsd(res.parsed.revenueMinor / 100) : '—',
                ],
                ['User IP', res.parsed.ip ?? '—'],
              ]}
            />
          </div>
          <Advanced label="Every field we read" hint="what the settings can map">
            <pre className="max-h-60 overflow-auto rounded-[10px] border border-hairline bg-canvas p-3 text-[12px] leading-relaxed">
              {JSON.stringify(res.fields, null, 2)}
            </pre>
          </Advanced>
        </div>
      )}
    </section>
  );
}

function Check2({ ok, label, detail }: { ok: boolean; label: string; detail: string }) {
  return (
    <li className="grid grid-cols-[20px_minmax(0,1fr)] items-start gap-x-3 gap-y-0.5 px-3.5 py-2.5 sm:grid-cols-[20px_140px_minmax(0,1fr)]">
      <span
        className={`mt-px grid h-5 w-5 place-items-center rounded-full text-white ${ok ? 'bg-mint-deep' : 'bg-berry-deep'}`}
      >
        {ok ? <Check size={12} strokeWidth={3} /> : <X size={12} strokeWidth={3} />}
        <span className="sr-only">{ok ? 'Passed:' : 'Failed:'}</span>
      </span>
      <span className="font-semibold">{label}</span>
      <span
        className={`col-start-2 min-w-0 [overflow-wrap:anywhere] sm:col-start-3 ${ok ? 'text-graphite' : 'font-semibold text-berry-deep'}`}
      >
        {detail}
      </span>
    </li>
  );
}
